/**
 * `bankAccounts.*` — first-class multi-row bank-account CRUD + the
 * step-up-gated change-bank-details flow.
 *
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §1
 */
import { z } from "zod";
import { denominationCount, isoDate, moneyPaise, signedPaise, uuid } from "../_common.js";

// ── inputs ──────────────────────────────────────────────────────────

const bankAccountType = z.enum(["bank", "cash", "credit_card"]);

/** Holder type — Company is the default; the rest cover founder/partner/director
    splits the GST/TDS challan signatory + invoice-print narration uses. */
const holderType = z.enum(["company", "founder_personal", "partner", "director", "other"]);

/** Optional KYC fields shared by bank + credit_card creates (added in
    migration 0023). Auto-populated from the IFSC lookup or filled by the
    operator. Cash drawers don't carry these — they have no bank relationship. */
const kycFields = {
  branch: z.string().max(120).optional(),
  micrCode: z.string().max(11).optional(),
  swiftBic: z.string().max(11).optional(),
  holderName: z.string().max(120).optional(),
  holderType: holderType.optional(),
} as const;

/** Discriminated union per ADR §1. */
export const createBankAccountInput = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("bank"),
    name: z.string().min(1).max(80),
    accountNumberPlaintext: z.string().min(6).max(64),
    ifscPlaintext: z.string().length(11),
    openingBalancePaise: moneyPaise,
    openingBalanceAsOf: isoDate,
    ...kycFields,
  }),
  z.object({
    type: z.literal("cash"),
    name: z.string().min(1).max(80),
    imprestAmountPaise: moneyPaise,
    openingBalancePaise: moneyPaise,
    openingBalanceAsOf: isoDate,
  }),
  z.object({
    type: z.literal("credit_card"),
    name: z.string().min(1).max(80),
    /** Last-4 of the PAN (server stores the wrapped value; never logs). */
    accountNumberPlaintext: z.string().min(4).max(20),
    creditLimitPaise: moneyPaise,
    statementCycleDay: z.number().int().min(1).max(31),
    openingBalancePaise: moneyPaise,
    openingBalanceAsOf: isoDate,
    ...kycFields,
  }),
]);

export const renameBankAccountInput = z.object({
  bankAccountId: uuid,
  name: z.string().min(1).max(80),
});

export const archiveBankAccountInput = z.object({
  bankAccountId: uuid,
});

export const changeBankDetailsInput = z.object({
  bankAccountId: uuid,
  accountNumberPlaintext: z.string().min(6).max(64),
  ifscPlaintext: z.string().length(11),
});

/**
 * `markCounted` — bump `last_counted_at` on a `type=cash` drawer after a
 * zero-variance physical count. The optional `countedAt` ISO datetime
 * lets back-dated counts land (e.g., the operator counted yesterday and
 * is logging today); omit it to default to the server's `clock.now()`.
 *
 * The `denominations`, `bookBalancePaise`, and `countedPaise` fields are
 * optional reconciliation evidence written into the `audit_entries.metadata`
 * payload — the count-history surface reads them back to render the
 * "book / counted / variance" reconciliation strip + denomination grid
 * in the expanded row. Optional so seed scripts + legacy callers that
 * don't have the data don't break; the SPA always passes them.
 */
export const markCountedInput = z.object({
  accountId: uuid,
  countedAt: z.string().datetime().optional(),
  bookBalancePaise: signedPaise.optional(),
  countedPaise: signedPaise.optional(),
  denominations: denominationCount.optional(),
});

export const markCountedOutput = z.object({
  accountId: uuid,
  countedAt: z.string(),
});

export const getBankAccountInput = z.object({
  bankAccountId: uuid,
  reveal: z.boolean().optional(),
});

export const listBankAccountsInput = z.object({
  includeArchived: z.boolean().optional(),
});

// ── outputs ─────────────────────────────────────────────────────────

const bankAccountShape = z.object({
  id: uuid,
  name: z.string(),
  type: bankAccountType,
  accountNumberLast4: z.string().nullable(),
  accountNumberPlaintext: z.string().nullable(),
  ifscPlaintext: z.string().nullable(),
  openingBalancePaise: moneyPaise,
  openingBalanceAsOf: isoDate,
  imprestAmountPaise: moneyPaise.nullable(),
  creditLimitPaise: moneyPaise.nullable(),
  statementCycleDay: z.number().int().nullable(),
  /** KYC fields (migration 0023). Auto-populated from IFSC lookup or operator. */
  branch: z.string().nullable(),
  micrCode: z.string().nullable(),
  swiftBic: z.string().nullable(),
  holderName: z.string().nullable(),
  holderType: holderType.nullable(),
  bankDetailsChangedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
  /** When the row was last updated — drives "Last edited X" copy in the edit composer head. */
  updatedAt: z.string(),
});

export const createBankAccountOutput = z.object({
  account: bankAccountShape,
});

export const renameBankAccountOutput = z.object({
  account: bankAccountShape,
});

export const archiveBankAccountOutput = z.object({
  account: bankAccountShape,
});

export const changeBankDetailsOutput = z.object({
  account: bankAccountShape,
  coolingOffEndsAt: z.string(),
});

export const getBankAccountOutput = z.object({
  account: bankAccountShape,
  /** Signed: a cash drawer can legitimately swing negative after a
   *  retro-posted reversal of an inbound transfer, or in legacy data
   *  predating the over-balance guard (commit cdc5916). */
  runningBalancePaise: signedPaise,
});

const listRow = z.object({
  id: uuid,
  name: z.string(),
  type: bankAccountType,
  accountNumberLast4: z.string().nullable(),
  runningBalancePaise: signedPaise,
  imprestAmountPaise: moneyPaise.nullable(),
  creditLimitPaise: moneyPaise.nullable(),
  statementCycleDay: z.number().int().nullable(),
  nextStatementOn: isoDate.nullable(),
  /**
   * ISO datetime of the last `changeBankDetails` post (ADR-0008). The
   * SPA reads this to surface a live "cooling-off in effect" hint on the
   * transfer composer; the backend refuses outbound posts within 24h of
   * this stamp regardless. Null when the account has never had its
   * encrypted credentials rotated.
   */
  bankDetailsChangedAt: z.string().nullable(),
  archivedAt: z.string().nullable(),
});

export const listBankAccountsOutput = z.object({
  rows: z.array(listRow),
  totals: z.object({
    /** Sum across bank + cash accounts. Signed because any one cash
     *  drawer can be negative (over-balance legacy rows). */
    cashOnHandPaise: signedPaise,
    /** Sum of credit_card liabilities. Stored magnitude is positive; SPA
     *  flips for display. Signed here for symmetry / forward-compat. */
    cardLiabilityPaise: signedPaise,
  }),
});

// ── derived TS types ──────────────────────────────────────────────
export type CreateBankAccountInput = z.infer<typeof createBankAccountInput>;
export type CreateBankAccountOutput = z.infer<typeof createBankAccountOutput>;
export type RenameBankAccountInput = z.infer<typeof renameBankAccountInput>;
export type ArchiveBankAccountInput = z.infer<typeof archiveBankAccountInput>;
export type ChangeBankDetailsInput = z.infer<typeof changeBankDetailsInput>;
export type MarkCountedInput = z.infer<typeof markCountedInput>;
export type MarkCountedOutput = z.infer<typeof markCountedOutput>;
export type GetBankAccountInput = z.infer<typeof getBankAccountInput>;
export type GetBankAccountOutput = z.infer<typeof getBankAccountOutput>;
export type ListBankAccountsInput = z.infer<typeof listBankAccountsInput>;
export type ListBankAccountsOutput = z.infer<typeof listBankAccountsOutput>;
export type BankAccountListRow = z.infer<typeof listRow>;
